import React, { useRef, useEffect, useState, useCallback } from 'react';
import WheelEngine from '../lib/wheel-engine';
import { useLanguage } from '../contexts/LanguageContext';

const SpinWheel = ({ 
  entries = [], 
  onSpinEnd, 
  onSpinStart,
  onBeforeSpin, // Called BEFORE spin starts, return false to prevent spin
  sounds = true,
  soundStyle = 'classic',
  victorySound = 'fanfare',
  spinDuration = 5000,
  colors = null,
  textColor = '#FFFFFF',
  className = '',
  triggerSpin = 0, // When this increments, trigger a spin
  isSpinning: externalIsSpinning, // External spinning state (from parent)
  onSpinningChange // Callback to update parent's spinning state
}) => {
  const canvasRef = useRef(null);
  const wheelRef = useRef(null);
  const [internalIsSpinning, setInternalIsSpinning] = useState(false);
  const [spinBlockReason, setSpinBlockReason] = useState(null);
  const lastTriggerSpinRef = useRef(0); // Track last processed triggerSpin value
  const { t } = useLanguage();
  
  // Use external state if provided, otherwise use internal state
  const isSpinning = externalIsSpinning !== undefined ? externalIsSpinning : internalIsSpinning;
  const setIsSpinning = (value) => {
    setInternalIsSpinning(value);
    if (onSpinningChange) {
      onSpinningChange(value);
    }
  };

  // Initialize wheel engine
  useEffect(() => {
    if (!canvasRef.current) return;

    const wheel = new WheelEngine(canvasRef.current, {
      entries: entries,
      sounds: sounds,
      soundStyle: soundStyle,
      victorySound: victorySound,
      spinDuration: spinDuration,
      colors: colors,
      textColor: textColor,
      onSpinStart: () => {
        setIsSpinning(true);
        setSpinBlockReason(null);
        onSpinStart?.();
      },
      onSpinEnd: (result) => {
        setIsSpinning(false);
        onSpinEnd?.(result);
      },
    });

    wheelRef.current = wheel;

    // Handle window resize
    const handleResize = () => wheel.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      wheel.destroy();
    };
  }, []);

  // Update sound style when it changes
  useEffect(() => {
    if (wheelRef.current) {
      wheelRef.current.setSoundStyle(soundStyle);
    }
  }, [soundStyle]);

  // Update victory sound when it changes
  useEffect(() => {
    if (wheelRef.current) {
      wheelRef.current.setVictorySound(victorySound);
    }
  }, [victorySound]);

  // Update entries when they change - filter only enabled entries
  useEffect(() => {
    if (wheelRef.current && !wheelRef.current.isDestroyed && wheelRef.current.canvas && wheelRef.current.ctx) {
      // Only include enabled entries in the wheel
      const enabledEntries = entries.filter(e => e.enabled !== false);
      wheelRef.current.setEntries(enabledEntries);
      // Update block reason when entries change
      setSpinBlockReason(wheelRef.current.getSpinBlockReason());
    }
  }, [entries]);

  // Update sounds setting
  useEffect(() => {
    if (wheelRef.current) {
      wheelRef.current.setSounds(sounds);
    }
  }, [sounds]);

  // Update onSpinEnd callback when it changes (important for settings changes)
  useEffect(() => {
    if (wheelRef.current) {
      wheelRef.current.setOnSpinEnd((result) => {
        setIsSpinning(false);
        onSpinEnd?.(result);
      });
    }
  }, [onSpinEnd]);

  // Trigger spin when triggerSpin prop changes (for countdown functionality)
  useEffect(() => {
    // Only trigger if triggerSpin increased from last processed value
    if (triggerSpin > lastTriggerSpinRef.current && wheelRef.current && !isSpinning) {
      const blockReason = wheelRef.current.getSpinBlockReason();
      if (!blockReason) {
        lastTriggerSpinRef.current = triggerSpin; // Mark as processed
        wheelRef.current.spin();
      }
    }
  }, [triggerSpin, isSpinning]);

  const handleSpin = useCallback(() => {
    if (wheelRef.current) {
      const blockReason = wheelRef.current.getSpinBlockReason();
      if (blockReason) {
        setSpinBlockReason(blockReason);
        return;
      }
      // Check onBeforeSpin callback - if it returns false, don't spin
      if (onBeforeSpin) {
        const shouldSpin = onBeforeSpin();
        if (shouldSpin === false) {
          return; // Don't spin, countdown or other logic will handle it
        }
      }
      wheelRef.current.spin();
    }
  }, [onBeforeSpin]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSpin();
    }
  }, [handleSpin]);

  // Determine button state and text
  const getButtonState = () => {
    if (isSpinning) {
      return { disabled: true, text: t('wheel.spinning'), className: 'spinning' };
    }
    if (entries.length === 0) {
      return { disabled: true, text: t('wheel.spin'), className: 'disabled' };
    }
    if (entries.length === 1) {
      return { disabled: true, text: '🎉', className: 'winner' };
    }
    return { disabled: false, text: t('wheel.spin'), className: '' };
  };

  const buttonState = getButtonState();

  return (
    <div className={`wheel-container ${className}`}>
      <div 
        className="wheel-wrapper"
        role="application"
        aria-label={t('a11y.wheelCanvas')}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <canvas
          ref={canvasRef}
          className="wheel-canvas"
          data-testid="wheel-canvas"
          style={{ width: '100%', height: '100%' }}
        />
      </div>
      <button
        onClick={handleSpin}
        disabled={buttonState.disabled}
        className={`spin-button ${buttonState.className}`}
        aria-label={t('a11y.spinButton')}
        aria-busy={isSpinning}
        data-testid="spin-button"
      >
        {buttonState.text}
      </button>
      
      {/* Spin block reason message */}
      {spinBlockReason === 'singleEntry' && (
        <p className="spin-message" role="status">
          {t('wheel.winner')} 🎉
        </p>
      )}
    </div>
  );
};

export default SpinWheel;
